import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import { theme } from '../../theme';

function Announcements() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', priority: 'normal', expires_at: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await hrms.getAnnouncements();
      setAnnouncements(r.data || []);
    } catch { setAnnouncements([]); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.title || !form.content) { alert('Title and content are required.'); return; }
    setSaving(true);
    try {
      const payload = { ...form, announcement_type: 'general' };
      if (modal?.id) {
        await hrms.updateAnnouncement(modal.id, payload);
      } else {
        await hrms.createAnnouncement(payload);
      }
      setModal(null);
      load();
    } catch (e) { alert('Failed to save announcement: ' + (e.response?.data?.detail || e.message)); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await hrms.deleteAnnouncement(id);
      load();
    } catch { alert('Failed to delete.'); }
  };

  const inp = { width: '100%', padding: '0.6rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', marginBottom: '0.75rem', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: theme.text, marginBottom: '0.35rem' };

  const priorityColors = { high: theme.danger, normal: theme.info, low: theme.success };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Announcements</h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Company-wide announcements</p>
        </div>
        <Button variant="primary" onClick={() => { setModal({}); setForm({ title: '', content: '', priority: 'normal', expires_at: '' }); }}>+ New Announcement</Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: theme.textMuted }}>Loading...</div>
      ) : announcements.length === 0 ? (
        <Card><p style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem 0' }}>No announcements yet</p></Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {announcements.map((ann) => (
            <Card key={ann.id} style={{ borderLeft: `4px solid ${priorityColors[ann.priority || 'normal'] || theme.info}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: theme.text }}>{ann.title}</h3>
                    <Badge status={ann.priority || 'normal'} label={ann.priority ? ann.priority.charAt(0).toUpperCase() + ann.priority.slice(1) : 'Normal'} />
                  </div>
                  <p style={{ margin: 0, color: theme.gray, fontSize: '0.875rem', lineHeight: 1.6 }}>{ann.content}</p>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>
                    {ann.created_at ? new Date(ann.created_at).toLocaleDateString() : ''}
                    {ann.expires_at && ` · Expires: ${new Date(ann.expires_at).toLocaleDateString()}`}
                    {ann.created_by_name && ` · By: ${ann.created_by_name}`}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                  <Button size="sm" variant="outline" onClick={() => { setModal(ann); setForm({ title: ann.title, content: ann.content, priority: ann.priority || 'normal', expires_at: ann.expires_at || '' }); }}>Edit</Button>
                  <Button size="sm" variant="danger" onClick={() => handleDelete(ann.id)}>Delete</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={!!modal} onClose={() => setModal(null)} title={modal?.id ? 'Edit Announcement' : 'New Announcement'} maxWidth="600px"
        footer={<><Button variant="outlineGray" onClick={() => setModal(null)}>Cancel</Button><Button variant="primary" loading={saving} onClick={handleSave}>Save</Button></>}>
        <label style={lbl}>Title *</label>
        <input style={inp} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Announcement title" />
        <label style={lbl}>Content *</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: '120px' }} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Write your announcement..." />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div>
            <label style={lbl}>Priority</label>
            <select style={inp} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Expires At</label>
            <input style={inp} type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default Announcements;
